import { useCallback, useMemo } from "react";
import { DirectUploadResult } from "helpers/activeStorage";
import { AttachmentRow, ResourceAttachment } from "features/Resources/Shared/attachments";
import { useFileUploads } from "features/Resources/Shared/hooks/useFileUploads";

// Immediate tray for auto-save / edit surfaces: each upload/removal persists on
// its own (attach/detach). Server set is the source of truth; local state holds
// only in-flight uploads, so there's nothing to reconcile.
export function useAutoSavedAttachments(
  existing: ResourceAttachment[] | undefined,
  attach: (upload: DirectUploadResult) => void,
  detach: (attachmentId: number) => void,
) {
  const server = useMemo(() => existing ?? [], [existing]);
  const baseBytes = server.reduce((sum, a) => sum + a.byteSize, 0);
  const uploads = useFileUploads({ baseBytes, onUploaded: (result) => attach(result) });

  const items: AttachmentRow[] = useMemo(
    () => [
      ...server.map((a) => ({
        key: `existing-${a.id}`,
        filename: a.filename,
        byteSize: a.byteSize,
        status: "done" as const,
        progress: 100,
        url: a.url,
      })),
      ...uploads.inFlight,
    ],
    [server, uploads.inFlight],
  );

  const remove = useCallback(
    (key: string) => {
      if (uploads.inFlight.some((r) => r.key === key)) {
        uploads.cancel(key);
        return;
      }
      const id = Number(key.replace("existing-", ""));
      // Negative id = an optimistic placeholder not yet persisted; leave it
      // visible until the attach resolves rather than sending a no-op detach.
      if (id < 0) return;
      detach(id);
    },
    [uploads, detach],
  );

  return { items, addFiles: uploads.add, remove, isUploading: uploads.isUploading };
}
